import React, { FC, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18next';
import moment from 'moment';

import { PageWrapper } from '@components/app/PageWrapper/PageWrapper';
import { useDebounce } from '@components/shared/customHooks/useDebounce';
import { Loader } from '@components/shared/Loader/Loader';
import { useInjection } from '@context/inversify-context-provider';
import AdminAppointmentPicker from '@features/oneOnboardingAdmin/components/AdminAppointmentPicker/AdminAppointmentPicker';
import { FilterName } from '@features/oneOnboardingAdmin/components/DashboardFilters/DashboardFiltersConfigs';
import SoftDeletedApplicationsTable from '@features/oneOnboardingAdmin/components/SoftDeletedApplicationsTable/SoftDeletedApplicationsTable';
import { getTenantDefaultLanguage } from '@helpers/tenant.helper';
import { useAllForDashboard, useSoftDeletedApplications } from '@hooks/apiHooks';
import { TYPES } from '@libs/InversifyType';
import { CheckUser, UserModel, UserRole } from '@models/user.model';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { store } from '@store/index';
import { RootState } from '@store/rootReducer';
import { dashboardSlice } from '@store/slices/dashboard.slice';
import { oneOnboardingSlice } from '@store/slices/oneOnboarding.slice';
import { snackbarSlice } from '@store/slices/snackbar';

import AdminContainer from '../../../../components/admin/AdminContainer/AdminContainer';
import { AamBackendApi } from '../../../../libs/aamBackendApi';
import { DropDownOptions } from '../../../oneOnboarding/interfaces';
import { useSorting } from '../../components/AdminDashboardTable/useSorting';
import AdminTableSearch from '../../components/AdminTableSearch/AdminTableSearch';
import { DashboardFilters } from '../../components/DashboardFilters/DashboardFilters';
import DashboardFiltersChipOverview from '../../components/DashboardFilters/DashboardFiltersChipOverview';
import { selectedValuesReducer } from '../../components/DashboardFilters/dashboardFiltersReducer';
import {
  useAggregatedDeletionReasonFilter,
  useAggregatedStatusFilters,
  useAggregatedStepFilters,
} from '../AdminDashboard/adminDashboardHooks';

const DEBOUNCE_TIME = 500;

const defaultStartDate = (offsetFromConfig?: number) => {
  const offset = offsetFromConfig ? -offsetFromConfig : -7;

  return moment.utc().startOf('day').add(offset, 'days').format('YYYY-MM-DD');
};

const defaultEndDate = moment.utc().format('YYYY-MM-DD');

export interface StepFilterModel {
  name: string;
  step?: string | string[];
  label: string;
  hasRejectedDocuments?: boolean | undefined;
  hasMandatoryDocumentsVerified?: boolean | undefined;
  inactivity?: number | undefined;
  userStatus?: string;
  withStuckCandidates?: boolean;
}
export interface StatusFilterModel {
  status: string;
  label: string;
}

export interface DeletionReasonFilterModel {
  status: string;
  label: string;
}
export interface FiltersVisibility {
  name: string;
  visible: boolean;
}

const softDeletedApplicationsFilters: FiltersVisibility[] = [
  { name: FilterName.APPLICATION, visible: true },
  { name: FilterName.BRANCH, visible: true },
  { name: FilterName.JOURNEY, visible: true },
  { name: FilterName.DELETED_REASON, visible: true },
  { name: FilterName.LOCATION, visible: false },
  { name: FilterName.REGION1, visible: false },
  { name: FilterName.ONBOARDING_STATUS, visible: false },
  { name: FilterName.CAMPAIGN, visible: false },
  { name: FilterName.ONBOARDING_STEP, visible: false },
  { name: FilterName.INTEGRATION_STATE, visible: false },
  { name: FilterName.BLOB_EXPORT_STATUS, visible: false },
  { name: FilterName.IMPORT_BY_ME, visible: false },
  { name: FilterName.FAILED_MAILS, visible: false },
  { name: FilterName.SITE_AS_LOCATION, visible: false },
  { name: FilterName.FAILED_MAILS, visible: false },
];
export const SoftDeletedApplications: FC = () => {
  const { adminDashboardFilters: adminDashboardFiltersValue } = useSelector((store: RootState) => store.oneOnboarding);
  const adminDashboardFilters = useDebounce(adminDashboardFiltersValue, DEBOUNCE_TIME);
  const [filterClientOrganizationId, setFilterClientOrganizationId] = useState<any>();
  const [filterJourneyId, setFilterJourneyId] = useState<any>();
  const { userId: recruiterId, userRole } = useSelector((state: RootState) => state.authAdmin);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState<CheckUser | null>(null);
  const [controlsFetching] = useState(false);
  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const { data: dashboardData } = useAllForDashboard(tenantId);
  const { configurations: tenantConfigurations } = dashboardData ?? {};
  const [selectedConfigurations, setSelectedConfigurations] = useState<any[]>([]);
  const [tenantClientOrganizations, setTenantClientOrganizations] = useState<any[]>([]);
  const [deletedApplicationsAccess, setDeletedApplicationsAccess] = useState<boolean>(true);
  const [filterSidebarActive, setFilterSidebarActive] = useState(false);
  const [selectedValues, dispatch] = useReducer(selectedValuesReducer, adminDashboardFilters);
  const dropdownOptions: DropDownOptions = {
    region1: [],
    AFFILIATION: [],
    JOB_TYPE: [],
    location: [],
  };
  const dateFormat = 'dd/MM/yyyy';

  const filtersVisibility: FiltersVisibility[] = softDeletedApplicationsFilters;

  const allowedRoles = [UserRole.adeccoStaff, UserRole.adeccoConfig, UserRole.adeccoStaffAndAdmin];
  const canRestoreCandidate = userRole !== UserRole.adeccoStaff;

  const { t } = useTranslation(['recruiter', 'control-items']);
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const storeDispatch = useDispatch();
  const history = useHistory();
  const { order, orderBy, setOrder, setOrderBy } = useSorting();

  const configuration = useMemo(
    () => (filterJourneyId ? tenantConfigurations?.find((config) => config.id === filterJourneyId) : undefined),
    [filterJourneyId, tenantConfigurations],
  );
  const { journeyId, ...otherFilters } = adminDashboardFilters;

  const handleLanguageChange = (languages: any[]) => {
    const languageCodes = languages?.map((lang: any) => lang.code);
    if (!languageCodes?.includes(i18n.language)) {
      const defaultLanguage = languages?.find((lang: any) => lang.isDefault === true);
      const languageToSet = defaultLanguage?.code || getTenantDefaultLanguage(tenantId);
      i18n.changeLanguage(languageToSet);
      localStorage.setItem('i18nextLng', languageToSet);
    }
  };

  const dispatchData = ({ languages, rejectReasons }: any) => {
    storeDispatch(oneOnboardingSlice.actions.setLanguages(languages));
    storeDispatch(oneOnboardingSlice.actions.setRejectReasons(rejectReasons));
  };

  const processConfigurations = (configurations: any[]) => {
    const clientOrganizationMap: any = {};
    configurations?.forEach((itm: any) => {
      clientOrganizationMap[itm.clientOrganization.name] = {
        ...itm.clientOrganization,
        journeys: configurations?.filter((conf) => conf.clientOrganization.id === itm.clientOrganization.id),
      };
    });

    setTenantClientOrganizations(() =>
      Object.values(clientOrganizationMap).sort((a: any, b: any) => a.name.localeCompare(b.name)),
    );

    setSelectedConfigurations(() =>
      configurations
        ?.map((x: any) => ({
          ...x,
          displayConfigurationName: x.clientOrganization?.displayName ?? x.clientOrganization?.name,
        }))
        .flat(),
    );
  };

  const { appointmentStartOffset, searchOnboardingEndpointVersion } = useSelector(
    (state: RootState) => state.featureConfiguration.appSettings,
  );

  const { appointmentStartDate, appointmentEndDate } = useSelector((state: RootState) => state.dashboard);

  const [onboardingDeletedAtInterval, setOnboardingDeletedAtInterval] = useState({
    onboardingCreatedAtStartDate: appointmentStartDate
      ? appointmentStartDate
      : defaultStartDate(appointmentStartOffset),
    onboardingCreatedAtEndDate: appointmentEndDate ? appointmentEndDate : defaultEndDate,
  });

  // setting default appointment dates
  useEffect((): any => {
    if (!appointmentStartDate) {
      store.dispatch(dashboardSlice.actions.setAppointmentStartDate(defaultStartDate(appointmentStartOffset)));
    }

    if (!appointmentEndDate) {
      store.dispatch(dashboardSlice.actions.setAppointmentEndDate(defaultEndDate));
    }
  }, [appointmentStartDate, appointmentEndDate, appointmentStartOffset]);

  const { isLoading: isLoadingUsers, data: responseUsersData, refetch } = useSoftDeletedApplications({
    filters: adminDashboardFilters,
    page,
    order,
    orderBy,
    onboardingDeletedAtInterval: {
      onboardingDeletedAtStartDate: onboardingDeletedAtInterval.onboardingCreatedAtStartDate,
      onboardingDeletedAtEndDate: onboardingDeletedAtInterval.onboardingCreatedAtEndDate,
    },
    rowsPerPage,
    tenantId,
    otherFilters,
    journeyId,
    searchOnboardingEndpointVersion,
  });

  const [users, total] = useMemo(() => {
    if (responseUsersData) {
      return [responseUsersData.results, responseUsersData.total, { searchParams: responseUsersData.searchParams }];
    }
    return [[], 0, { searchParams: {} }];
  }, [responseUsersData]);

  if (!deletedApplicationsAccess) {
    history.push('/oo/recruiter/unauthorized');
  }

  const filterControls = {
    isActive: filterSidebarActive,
    onClose: () => setFilterSidebarActive(false),
    onOpen: () => setFilterSidebarActive(true),
  };

  useEffect(() => {
    storeDispatch(
      dashboardSlice.actions.setAppointmentStartDate(onboardingDeletedAtInterval.onboardingCreatedAtStartDate),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingDeletedAtInterval.onboardingCreatedAtStartDate]);

  useEffect(() => {
    storeDispatch(dashboardSlice.actions.setAppointmentEndDate(onboardingDeletedAtInterval.onboardingCreatedAtEndDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingDeletedAtInterval.onboardingCreatedAtEndDate]);

  useEffect((): any => {
    return () => {
      store.dispatch(oneOnboardingSlice.actions.resetAdminDashboardFilters());
    };
  }, []);

  useEffect(() => {
    if (dashboardData) {
      const { languages, configurations, rejectReasons } = dashboardData;
      handleLanguageChange(languages);
      dispatchData({ languages, configurations, rejectReasons });
      processConfigurations(configurations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);
  useEffect((): any => {
    let isMounted = true;
    const call = async () => {
      if (recruiterId) {
        const user = (await aamBackendApi.fetchUser(tenantId, recruiterId)) as UserModel;
        if (isMounted) {
          if (!user) {
            setDeletedApplicationsAccess(false);
            return;
          }
          if (user.role === UserRole.adeccoAdmin) {
            history.push('/oo/recruiter/permissions');
          }
          setDeletedApplicationsAccess(allowedRoles.includes(user.role));
        }
      }
    };
    call().catch((err) => window.alert(err));
    return () => (isMounted = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, recruiterId, aamBackendApi]);

  const stepFiltersItems = useAggregatedStepFilters(tenantConfigurations, filterClientOrganizationId, filterJourneyId);

  const statusFiltersItems = useAggregatedStatusFilters(
    tenantConfigurations,
    filterClientOrganizationId,
    filterJourneyId,
  );

  const deletionReasonItems = useAggregatedDeletionReasonFilter(
    tenantConfigurations,
    filterClientOrganizationId,
    filterJourneyId,
  );

  const handleSelectUser = (user: any) => {
    if (selectedUser === user) {
      setSelectedUser(null);
    } else {
      setSelectedUser(user);
    }
  };

  const handleRestoreUser = async () => {
    if (selectedUser && canRestoreCandidate) {
      if (window.confirm(t('GENERAL.CANDIDATES_OVERVIEW.restoreConfirm'))) {
        try {
          setSelectedUser(null);
          await aamBackendApi.restoreDeletedOnboarding(
            selectedUser.clientOrganizationId,
            selectedUser.configurationId,
            selectedUser.applicationId,
            selectedUser.userId,
          );
          storeDispatch(
            snackbarSlice.actions.showSuccess({
              message: `${t('GENERAL.CANDIDATES_OVERVIEW.restoreSuccess')}`,
            }),
          );
          refetch();
        } catch (err) {
          storeDispatch(
            snackbarSlice.actions.showError({
              message: err?.message || t('GENERAL.CANDIDATES_OVERVIEW.restoreError'),
            }),
          );
        }
      }
    }
  };

  if (!selectedConfigurations[0]) {
    return <Loader />;
  }
  return (
    <div className="admin">
      <PageWrapper
        featureName="softDeletedCandidates"
        pageName="softDeletedCandidates"
        sideMenu
        headerConfig={{
          pageName: t('recruiter:GENERAL.CANDIDATES_OVERVIEW.softDeletedCandidates'),
          languagePicker: true,
        }}
      >
        <DashboardFilters
          selectedValues={selectedValues}
          dispatch={dispatch}
          filters={[]}
          dropdownOptions={dropdownOptions}
          stepFiltersOptions={stepFiltersItems}
          onboardingStatusOptions={statusFiltersItems}
          disabledDropDowns={{
            LOCATION: !adminDashboardFilters?.regions?.length,
          }}
          selectedConfiguration={configuration}
          allClientOrganizations={tenantClientOrganizations}
          allJourneys={selectedConfigurations?.map((x) => ({
            recruiterName: x.recruiterName,
            id: JSON.stringify({
              id: x.id,
              clientOrganizationId: x.clientOrganizationId,
            }),
          }))}
          onClientOrganizationChange={(clientOrganizationId: string) =>
            setFilterClientOrganizationId(clientOrganizationId)
          }
          onJourneyChange={(journeyId: string) => {
            setFilterClientOrganizationId(JSON.parse(journeyId).clientOrganizationId);
            setFilterJourneyId(JSON.parse(journeyId).id);
          }}
          controlsFetching={controlsFetching}
          filterControls={filterControls}
          filtersVisibility={filtersVisibility}
          resetPageNumber={() => {
            setPage(0);
          }}
          deletionReasonItems={deletionReasonItems}
        />

        <AdminContainer>
          <Grid container alignItems="baseline" justifyContent="space-between" style={{ marginTop: '20pt' }}>
            <Grid item>
              <Box display="flex" alignItems="baseline">
                <Grid item style={{ marginRight: 16, alignSelf: 'flex-start' }}>
                  <div className="tag-ds input-wrapper   ">
                    <label className="caption">{t('recruiter:totalRecords')}</label>
                    <div style={{ marginTop: '8px' }}>{total}</div>
                  </div>
                </Grid>

                <AdminAppointmentPicker
                  dateFormat={dateFormat}
                  onboardingCreatedAtInterval={onboardingDeletedAtInterval}
                  setOnboardingCreatedAtInterval={setOnboardingDeletedAtInterval}
                />
                <AdminTableSearch handleDispatch={dispatch} />
              </Box>
            </Grid>
            <Grid item>
              <div className="tag-ds">
                <Box display="flex">
                  <button className="button-secondary" style={{ marginRight: 32 }} onClick={filterControls.onOpen}>
                    {t('recruiter:GENERAL.CANDIDATES_OVERVIEW.filters')}
                    <span className="material-icons right">filter_list</span>
                  </button>
                </Box>
              </div>
            </Grid>
          </Grid>
          <DashboardFiltersChipOverview dispatch={dispatch} resetPageNumber={() => setPage(0)} />
        </AdminContainer>
        {controlsFetching ? null : (
          <AdminContainer>
            {isLoadingUsers ? (
              <Box style={{ textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <SoftDeletedApplicationsTable
                  data={users}
                  allOrganizations={tenantClientOrganizations}
                  allJournies={selectedConfigurations}
                  dateFormat={dateFormat}
                  selectedUser={selectedUser}
                  handleRestoreUser={handleRestoreUser}
                  handleSelectUser={handleSelectUser}
                  pagination={{
                    page,
                    setPage,
                    total,
                    rowsPerPage,
                    setRowsPerPage,
                  }}
                  order={order}
                  orderBy={orderBy}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  canRestoreCandidate={canRestoreCandidate}
                />
              </>
            )}
          </AdminContainer>
        )}
      </PageWrapper>
    </div>
  );
};
